import React, { useState } from 'react';
import { Shield, Smartphone, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';

const TwoFactorVerification = ({ username, onSuccess, onBack, onCancel, hideNavigation = false }) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);

  const handleVerification = async (e) => {
    e.preventDefault();
    await handleAutoVerification();
  };

  // Auto-verify when 6 digits are entered
  const handleCodeChange = (e) => {
    const value = e.target.value;
    
    // Only allow numbers
    const numericValue = value.replace(/[^0-9]/g, '');
    
    // Update state with only numeric characters
    setVerificationCode(numericValue);
    setError(''); // Clear any previous errors
    
    // Auto-verify when 6 digits are entered (for authenticator app)
    if (!useBackupCode && numericValue.length === 6) {
      // Auto-verify immediately with the current numeric value
      handleAutoVerificationWithCode(numericValue);
    }
    // Auto-verify when 8 digits are entered (for backup codes)
    else if (useBackupCode && numericValue.length === 8) {
      // Auto-verify immediately with the current numeric value
      handleAutoVerificationWithCode(numericValue);
    }
  };

  // Separate function for auto-verification
  const handleAutoVerification = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Pass the verification code to the parent component
      await onSuccess(verificationCode.trim());
      
      // Keep loading state for a bit to show the verification process
      setTimeout(() => {
        setLoading(false);
      }, 500);
      
    } catch (error) {
      setError('Verification failed');
      toast.error('Verification failed');
      setLoading(false);
    }
  };

  // Function for auto-verification with specific code (avoids state timing issues)
  const handleAutoVerificationWithCode = async (code) => {
    if (!code || !code.trim()) {
      setError('Please enter the verification code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Pass the verification code to the parent component
      await onSuccess(code.trim());
      
      // Keep loading state for a bit to show the verification process
      setTimeout(() => {
        setLoading(false);
      }, 500);
      
    } catch (error) {
      setError('Verification failed');
      toast.error('Verification failed');
      setLoading(false);
    }
  };

  const handleBackupCode = async (e) => {
    e.preventDefault();
    await handleAutoVerification();
  };

  const handleBack = () => {
    setVerificationCode('');
    setError('');
    setUseBackupCode(false);
    if (onBack) onBack();
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
  };

  return (
    <div className="auth-page min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            {!hideNavigation && (
              <button
                onClick={handleBack}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <h1 className={`text-xl font-semibold text-gray-900 ${hideNavigation ? 'text-center flex-1' : ''}`}>
              Two-Factor Authentication
            </h1>
            {!hideNavigation && (
              <button
                onClick={handleCancel}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                ✕
              </button>
            )}
            {hideNavigation && <div className="w-9"></div>} {/* Spacer for centering when buttons are hidden */}
          </div>
        </div>

        <div className="p-6">
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Enter Verification Code</h2>
            <p className="text-gray-600">
              {useBackupCode 
                ? 'Enter one of your backup codes to access your account.'
                : 'Enter the 6-digit code from your authenticator app.'
              }
            </p>
          </div>

          <form onSubmit={useBackupCode ? handleBackupCode : handleVerification} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {useBackupCode ? 'Backup Code' : 'Verification Code'}
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={verificationCode}
                onChange={handleCodeChange}
                placeholder={useBackupCode ? "8-digit backup code" : "000000"}
                maxLength={useBackupCode ? 8 : 6}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1 text-center">
                {useBackupCode 
                  ? 'Enter the 8-digit backup code'
                  : 'Enter the 6-digit code from your authenticator app'
                }
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !verificationCode.trim()}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
            >
              {loading && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => setUseBackupCode(!useBackupCode)}
              className="w-full text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              {useBackupCode 
                ? 'Use authenticator app instead' 
                : 'Use backup code instead'
              }
            </button>
          </div>

          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              Having trouble? Contact your administrator for assistance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorVerification;
